<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance, nextTick } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { requestMagicLink } from '../api';
import { loadRecaptchaScript, executeRecaptcha } from '../services/recaptcha';
import { isValidEmail } from '../services/validators';

type OtpError = 'invalid_otp_token' | 'expired_otp_token' | 'used_otp_token';

const OTP_ERRORS: OtpError[] = ['invalid_otp_token', 'expired_otp_token', 'used_otp_token'];
const RECOVERY_QUERY_KEYS = ['otp_error', 'recovery_context', 'recovery_context_expires_at'];

const instance = getCurrentInstance();
const route = instance?.proxy?.$route;

const email = ref('');
const emailError = ref('');
const formError = ref('');
const loading = ref(false);
const success = ref(false);
const recaptchaReady = ref(false);
const expiresInSeconds = ref<number | null>(null);
const otpError = parseOtpError(route?.query.otp_error);
const recoveryContext = queryString(route?.query.recovery_context);

const pageTitle = computed(() => (success.value ? 'Check your email' : 'Sign in with email'));

const recoveryMessage = computed(() => {
  switch (otpError) {
    case 'expired_otp_token':
      return 'Your sign-in link has expired. Enter your email below and we’ll send you a new one.';
    case 'used_otp_token':
      return 'This sign-in link has already been used. Each link works only once. Enter your email below to request a new one.';
    case 'invalid_otp_token':
      return 'This sign-in link is invalid. Enter your email below and we’ll send you a new one.';
    default:
      return '';
  }
});

const linkLifetime = computed(() => {
  if (expiresInSeconds.value === null) return '';

  if (expiresInSeconds.value % 60 === 0) {
    const minutes = expiresInSeconds.value / 60;
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  return `${expiresInSeconds.value} ${expiresInSeconds.value === 1 ? 'second' : 'seconds'}`;
});

const topLinks = computed(() => {
  if (success.value) {
    return [{ url: '/users/sign_in', text: 'Back to sign in' }];
  }
  return [
    { url: '/users/sign_in', text: 'Sign In' },
    { url: '/users/password/new', text: 'Forgot your password?' },
  ];
});

const bottomLinks = computed(() => {
  if (success.value) return [];
  return [{ url: '/users/request', text: 'Not a member yet? Request access' }];
});

function queryString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function parseOtpError(value: unknown): OtpError | null {
  return typeof value === 'string' && OTP_ERRORS.includes(value as OtpError) ? (value as OtpError) : null;
}

function clearRecoveryQuery() {
  const url = new URL(window.location.href);
  RECOVERY_QUERY_KEYS.forEach(key => url.searchParams.delete(key));
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

function reportRecaptchaLoadFailure(error: unknown) {
  window.airbrake?.notify({
    error: error instanceof Error ? error : new Error('reCAPTCHA failed to load'),
    params: { component: 'MagicLinkNew' },
  });
}

async function sendMagicLink() {
  emailError.value = '';
  formError.value = '';

  if (!isValidEmail(email.value)) {
    emailError.value = 'Enter a valid email.';
    return;
  }

  if (!recaptchaReady.value) {
    formError.value = 'Security verification is unavailable. Please refresh and try again.';
    return;
  }

  loading.value = true;

  try {
    const token = await executeRecaptcha('MagicLinkRequest');
    if (!token) {
      formError.value = 'Security verification failed. Please refresh and try again.';
      return;
    }

    const response = await requestMagicLink(email.value.trim(), token, { recoveryContext });
    expiresInSeconds.value = response.expires_in;
    success.value = true;
    clearRecoveryQuery();
  } catch {
    formError.value = 'Unable to send sign-in link. Please try again.';
  } finally {
    loading.value = false;
  }
}

async function handleSubmit() {
  await sendMagicLink();
}

async function handleResend() {
  await sendMagicLink();
}

async function handleEditEmail() {
  success.value = false;
  formError.value = '';
  await nextTick();
  document.getElementById('magic-link-email')?.focus();
}

onMounted(async () => {
  const queryEmail = route?.query.email;
  if (typeof queryEmail === 'string') {
    email.value = queryEmail;
  }

  try {
    await loadRecaptchaScript();
    recaptchaReady.value = true;

    if (email.value && route?.query.auto_submit) {
      await handleSubmit();
    }
  } catch (error) {
    formError.value = 'Security verification could not load. Please refresh and try again.';
    reportRecaptchaLoadFailure(error);
  }
});
</script>

<template>
  <AuthLayout :title="pageTitle" :top-links="topLinks" :bottom-links="bottomLinks">
    <section
      v-if="recoveryMessage && !success"
      class="mb-5 rounded border border-sw-purple/60 bg-sw-purple/10 p-4 text-left text-white"
      role="alert"
      aria-labelledby="otp-recovery-title"
    >
      <h2 id="otp-recovery-title" class="m-0 mb-2 text-lg font-semibold leading-snug">Request a new sign-in link</h2>
      <p class="m-0 text-sm leading-relaxed">{{ recoveryMessage }}</p>
    </section>

    <div v-if="success" class="text-left text-white" role="status" aria-live="polite">
      <p class="mb-2 leading-relaxed">
        We sent a sign-in link to
        <strong class="break-words text-sw-purple">{{ email }}</strong
        >. Open it to access your account.
      </p>
      <p class="mb-4 text-sm text-white/75">The link expires in {{ linkLifetime }} and can only be used once.</p>

      <p v-if="formError" class="mb-3 text-sm leading-relaxed text-sw-error" role="alert">{{ formError }}</p>

      <div class="flex flex-col gap-3 border-t border-white/20 pt-4">
        <Button
          type="button"
          variant="outline"
          :loading="loading"
          :disabled="loading"
          loading-label="Resending..."
          @click.native="handleResend"
        >
          Resend sign-in link
        </Button>
        <button
          type="button"
          class="border-0 bg-transparent p-2 text-left text-sm font-medium text-sw-purple underline-offset-2 hover:underline"
          :disabled="loading"
          @click="handleEditEmail"
        >
          Use a different email
        </button>
      </div>
    </div>

    <form v-else novalidate @submit.prevent="handleSubmit">
      <p class="mb-4 text-sm leading-relaxed text-white/70">
        Enter your email and we’ll send you a secure sign-in link.
      </p>

      <Input
        id="magic-link-email"
        v-model="email"
        type="email"
        label="Email"
        placeholder="Email"
        autocomplete="email"
        required
        :disabled="loading"
        :error="emailError"
      />

      <p v-if="formError" class="m-0 mt-2 text-sm leading-relaxed text-sw-error" role="alert">{{ formError }}</p>

      <div class="mt-form-gap">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          :loading="loading"
          :disabled="loading"
          loading-label="Sending sign-in link..."
        >
          Send sign-in link
        </Button>
      </div>
    </form>
  </AuthLayout>
</template>
